/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B030060 売掛金残高一覧表
 */
import {
  SText,
  SCodeSelect,
  SFrame,
  SLabel,
  SNumberField,
  STextField,
  SFileButton,
  msgState,
} from "components";
import { B030060ShokaKbn, getSCode, SCode } from "messages/codes";
import { labels } from "messages/labels";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { focusState, useFocus } from "states";
import { download, yearMonth } from "../../services";
import { useNavigate } from "react-router-dom";
import {
  B000130BumonNmrFrom,
  B000130BumonNmrTo,
  B000140ShainNmrFrom,
  B000140ShainNmrTo,
  B000150UrikakeNmrFrom,
  B000150UrikakeNmrTo,
  catchFileError,
} from "clients/rest";
import { B030060GetExcel, B030060Request } from "clients/rest/b030/B030060";
import { B000150 } from "pages";
import { calledParam } from "services/codes";
import { getClientError, getClientMessage } from "messages";

// TODO yyyy/mmの入力方法確認後、実装。

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  urikakeYm: string;
  syokaKbn: SCode;
  bumonCdFrom: string;
  bumonNmrFrom: string;
  bumonCdTo: string;
  bumonNmrTo: string;
  shainCdFrom: string;
  shainNmrFrom: string;
  shainCdTo: string;
  shainNmrTo: string;
  urikakeCdFrom: string;
  urikakeshitenCdFrom: string;
  urikakeNmrFrom: string;
  urikakeCdTo: string;
  urikakeshitenCdTo: string;
  urikakeNmrTo: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  urikakeYm: yearMonth(),
  syokaKbn: getSCode(B030060ShokaKbn, "0"),
  bumonCdFrom: "",
  bumonNmrFrom: "",
  bumonCdTo: "",
  bumonNmrTo: "",
  shainCdFrom: "",
  shainNmrFrom: "",
  shainCdTo: "",
  shainNmrTo: "",
  urikakeCdFrom: "",
  urikakeshitenCdFrom: "",
  urikakeNmrFrom: "",
  urikakeCdTo: "",
  urikakeshitenCdTo: "",
  urikakeNmrTo: "",
};

const focusId = "B030060";

// B030060 売掛金残高一覧表
export const B030060 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  // 初期表示イベント
  useEffect(() => {
    focus(1);
  }, []);

  // Excel出力ボタンクリックイベント
  const handleClickExcelButton = () => {
    // フィールドの値取得
    const fields = methods.getValues();

    if (!fields.urikakeYm) {
      setMsg(getClientError("B030060_001"));
      return;
    }

    // リクエスト作成
    const param: B030060Request = {
      urikakeYm: fields.urikakeYm!,
      shokaKbn: fields.syokaKbn?.value!,
      bumonCdFrom: fields.bumonCdFrom,
      bumonCdTo: fields.bumonCdTo,
      shainCdFrom: fields.shainCdFrom,
      shainCdTo: fields.shainCdTo,
      urikakeCdFrom: fields.urikakeCdFrom,
      urikakeshitenCdFrom: fields.urikakeshitenCdFrom,
      urikakeCdTo: fields.urikakeCdTo,
      urikakeshitenCdTo: fields.urikakeshitenCdTo,
    };

    B030060GetExcel(param)
      .then((response) => {
        download(response);
        setMsg(getClientMessage("B030060_001"));
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="売掛金残高一覧表"
          gridTemplateColumns="7rem 6rem 4rem 10rem 5rem 7rem 4rem 8rem "
        >
          <SLabel required> 対象年月</SLabel>
          <SNumberField
            name="urikakeYm"
            label="対象年月"
            code
            required
            gridColumn="span 2"
            size={6}
            {...register(1)}
          />
          <SText align="left">月度</SText>

          <SLabel gridColumn="1">消化区分</SLabel>
          <SCodeSelect
            name="syokaKbn"
            label="消化区分"
            options={B030060ShokaKbn}
            required
            gridColumn="span 2"
            {...register(2)}
          />

          <SLabel gridColumn="1"> 売掛担当部門</SLabel>
          <SNumberField
            name="bumonCdFrom"
            label="売掛担当部門コード(開始)"
            code
            size={6}
            gridColumn="span 2"
            onBlur={() => B000130BumonNmrFrom(methods, navigate, setMsg)}
            {...register(3)}
          />
          <STextField
            name="bumonNmrFrom"
            label="売掛担当部門名称(開始)"
            size={20}
            disabled
          />
          <SText>~</SText>
          <SNumberField
            name="bumonCdTo"
            label="売掛担当部門コード(終了)"
            code
            size={6}
            gridColumn="span 2"
            onBlur={() => B000130BumonNmrTo(methods, navigate, setMsg)}
            {...register(4)}
          />
          <STextField
            name="bumonNmrTo"
            label="売掛担当部門名称(終了)"
            size={20}
            disabled
          />

          <SLabel gridColumn="1"> 売掛担当者</SLabel>
          <SNumberField
            name="shainCdFrom"
            label="売掛担当者コード(開始)"
            code
            size={7}
            gridColumn="span 2"
            onBlur={() => B000140ShainNmrFrom(methods, navigate, setMsg)}
            {...register(5)}
          />
          <STextField
            name="shainNmrFrom"
            label="売掛担当者名称(開始)"
            disabled
            size={40}
          />
          <SText>~</SText>
          <SNumberField
            name="shainCdTo"
            label="売掛担当者コード(終了)"
            code
            size={7}
            gridColumn="span 2"
            onBlur={() => B000140ShainNmrTo(methods, navigate, setMsg)}
            {...register(6)}
          />
          <STextField
            name="shainNmrTo"
            label="売掛担当者名称(終了)"
            size={40}
            disabled
          />

          <SLabel gridColumn="1"> 売掛先</SLabel>
          <SNumberField
            name="urikakeCdFrom"
            label="売掛先コード(開始)"
            code
            size={6}
            onBlur={() => B000150UrikakeNmrFrom(methods, navigate, setMsg)}
            {...register(7)}
          />
          <SNumberField
            name="urikakeshitenCdFrom"
            label="売掛支店コード(開始)"
            code
            size={4}
            dialog={<B000150 calledParam={calledParam.urikake} />}
            handleSubmit={(data: any) => {
              methods.setValue("urikakeCdFrom", data.tokuiCd);
              methods.setValue("urikakeshitenCdFrom", data.tokuishitenCd);
              methods.setValue("urikakeNmrFrom", data.tokuiNmr);
            }}
            onBlur={() => B000150UrikakeNmrFrom(methods, navigate, setMsg)}
            {...register(8)}
          />
          <STextField
            name="urikakeNmrFrom"
            label="売掛先名称(開始)"
            size={24}
            disabled
          />
          <SText>~</SText>
          <SNumberField
            name="urikakeCdTo"
            label="売掛先コード(終了)"
            code
            size={6}
            onBlur={() => B000150UrikakeNmrTo(methods, navigate, setMsg)}
            {...register(9)}
          />
          <SNumberField
            name="urikakeshitenCdTo"
            label="売掛支店コード(終了)"
            code
            size={4}
            dialog={<B000150 calledParam={calledParam.urikake} />}
            handleSubmit={(data: any) => {
              methods.setValue("urikakeCdTo", data.tokuiCd);
              methods.setValue("urikakeshitenCdTo", data.tokuishitenCd);
              methods.setValue("urikakeNmrTo", data.tokuiNmr);
            }}
            onBlur={() => B000150UrikakeNmrTo(methods, navigate, setMsg)}
            {...register(10)}
          />
          <STextField
            name="urikakeNmrTo"
            label="売掛先名称(終了)"
            size={24}
            disabled
          />

          <SFileButton
            label={labels.buttons.excel}
            onClick={handleClickExcelButton}
            gridColumn={5}
            gridRow={8}
            {...register(11)}
          />
        </SFrame>
      </FormProvider>
    </>
  );
};
